<template>
  <div>
    <div class="f-h f-w f-ac f-gap-1 bg-bg p-4 m-bx1 br-8">
      <div
        v-for="button in includedButtons.filter(b => !b.formattingKeyOnly)"
        :key="`wysiwyg-button-${currentInstance.uid}-${button.name}`"
      >
        <div v-if="button.divider" class="wysiwyg-divider" />
        <donut-selector-emoji
          v-else-if="button.name === 'emoji'"
          v-tooltip="button.label"
          :class="buttonClasses(button) + ' dark-gray'"
          @update:model-value="addEmoji"
        />
        <div
          v-else
          v-tooltip="button.label"
          :class="buttonClasses(button)"
          @click="clickButton(button)"
        >
          <i :class="`dark-gray mi ${button.name === 'help' ? 'md-18' : 'md-20'}`">{{ button.icon }}</i>
        </div>
      </div>
    </div>
    <div
      v-if="linkButton.selected"
      class="f-v p-x1 m-bx1 b-1 br-8 t-lg"
    >
      <div class="f-h f-ac m-b4">
        <label class="t bold m-l0 p-l4">Text</label>
        <donut-form-input
          ref="linkTextInput"
          name="wysiwygLinkText"
          :for="linkText"
          :validator="presenceValidator('Text')"
          class="flexed"
        >
          <input v-model="linkText" @keydown.enter.prevent="addLink">
        </donut-form-input>
      </div>
      <div class="f-h f-ac m-b4">
        <label class="t bold m-l0 p-l4">Link</label>
        <donut-form-input
          ref="linkUrlInput"
          name="wysiwygLinkUrl"
          :for="linkUrl"
          :validators="[presenceValidator('A url'), urlValidator()]"
          :validation-delay="1000"
          class="flexed"
        >
          <input v-model="linkUrl" @keydown.enter.prevent="addLink">
        </donut-form-input>
      </div>
      <donut-button class="sm fc-ae inv" @click="addLink">Add link</donut-button>
    </div>
    <div
      v-if="helpButton.selected"
      id="message-formatting-key"
      class="f-ac f-w m-bx1 w-0 w-min-100p"
    >
      <component
        :is="markdownItem.keyComponent || 'span'"
        v-for="(markdownItem, idx) in markdownItems"
        :key="`markdown-${idx}`"
        class="tip"
      >
        {{ markdownItem.keyDisplay }}
      </component>
    </div>
  </div>
</template>

<script setup>
  import { getCurrentInstance, ref } from 'vue';
  import { useStore } from 'vuex';
  import useValidators from 'composables/validators';
  import _ from 'lodash';

  const { state } = useStore();
  const { presenceValidator, urlValidator } = useValidators();

  const currentInstance = getCurrentInstance();

  const buttonClasses = (button) => {
    let res = 'wysiwyg-button p-v4 br-8 ta-c';
    res += button.name === 'help' ? ' p-h1' : ' p-h2';
    if (button.selected) res += ' selected';
    return res;
  };

  const linkText = ref('');
  const linkUrl = ref('');
  const linkTextInput = ref(null);
  const linkUrlInput = ref(null);

  // Note that a modelValue prop is not defined. It's read directly from the input element.
  const emit = defineEmits(['update:modelValue']);
  const props = defineProps({
    inputElement: { type: Object, required: true },
    ignore: { type: Array, default: () => [] },
  });

  // Wrap text in tags, like *hey* for bold. If no text is selected, it inserts the tags at the cursor: **.
  const wrap = (button) => {
    const el = props.inputElement;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const { tag } = button;
    el.focus();
    // If you just clicked the button and want to undo it, this removes the tags ('**' => '' or '*hey*' => 'hey')
    if (el.value.slice(start - tag.length, start) === tag && el.value.slice(end, end + tag.length) === tag) {
      // Select the tags along with the inner text, then reinsert just the inner text.
      el.setSelectionRange(start - tag.length, end + tag.length);
      document.execCommand('insertText', false, el.value.slice(start, end));
      // The start and end are now both minus the left tag.
      el.setSelectionRange(start - tag.length, end - tag.length);
    } else {
      document.execCommand('insertText', false, tag + el.value.slice(start, end) + tag);
      // The start and end are now both offset by the left side of the tag.
      el.setSelectionRange(start + tag.length, end + tag.length);
    }
    emit('update:modelValue', el.value);
  };

  const blockquote = () => {
    const el = props.inputElement;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const tag = '> ';
    // Move from the cursor to the beginning of the line to add the first tag there.
    let lineStart = start;
    while (lineStart > 0) {
      lineStart -= 1;
      if (el.value[lineStart] === '\n') break;
    }
    const oldStr = el.value.slice(lineStart, end);
    // If the selection ends on a newline, leave that final line break untouched so we don't quote
    // the line after the selection.
    const trailingNewline = oldStr.endsWith('\n');
    const body = trailingNewline ? oldStr.slice(0, -1) : oldStr;
    // Match each individual newline (and the start of the input) so every line — including the
    // blank lines between paragraphs — gets its own "> ". Slack merges consecutive quoted lines
    // into one continuous blockquote.
    const regex = lineStart === 0 ? '(\n|^)' : '(\n)';
    el.focus();
    // Select from the start of the line through the end of the selection, so we can reinsert the requoted text.
    el.setSelectionRange(lineStart, end);
    // Already quoted, remove the quote.
    if (new RegExp(`${regex}> `).test(body)) {
      const newStr = body.replaceAll(new RegExp(`${regex}> `, 'g'), '$1') + (trailingNewline ? '\n' : '');
      // execCommand is deprecated but widely used for wysiwygs across the web to preserve undo history.
      document.execCommand('insertText', false, newStr);
      // The start got moved back by one "> ", the end by possibly multiple of them.
      el.setSelectionRange(start - tag.length, end + newStr.length - oldStr.length);
    } else {
      // Not already quoted, add the quote.
      let newStr = body.replaceAll(new RegExp(regex, 'g'), `$1${tag}`);
      if (trailingNewline) newStr += '\n';
      document.execCommand('insertText', false, newStr);
      // The (original) start is offset by one "> ", the end is offset by possibly multiple of them.
      el.setSelectionRange(start + tag.length, end + newStr.length - oldStr.length);
    }
    emit('update:modelValue', el.value);
  };

  // Generic. Adds text at the selection place (replacing selected text, if any).
  const addText = (newStr) => {
    const el = props.inputElement;
    const start = el.selectionStart;
    el.focus();
    // Of note that this replaces whatever's selected, if anything.
    document.execCommand('insertText', false, newStr);
    // Move to after the new text.
    el.setSelectionRange(start + newStr.length, start + newStr.length);
    emit('update:modelValue', el.value);
  };

  const triggerAutocomplete = (button) => {
    addText(button.tag);
    props.inputElement.textcomplete.trigger(button.tag);
  };

  const addEmoji = (emojiObj) => {
    addText(`:${emojiObj.id}:`);
  };

  const clickButton = (button) => {
    if (button.selectable) button.selected = !button.selected;
    if (button.onClick) button.onClick(button);
    window.donut.track('WYSIWYG — Clicked Button', { button: button.name });
  };

  const linkButton = ref({
    name: 'link',
    icon: 'link',
    selectable: true,
    onClick: () => {
      const el = props.inputElement;
      if (el.selectionStart !== el.selectionEnd) {
        linkText.value = el.value.slice(el.selectionStart, el.selectionEnd);
      }
    },
    keyDisplay: '<http://link.com|Link>',
  });

  const addLink = () => {
    linkTextInput.value.forceValidate();
    linkUrlInput.value.forceValidate();
    if (state.errors.wysiwygLinkText || state.errors.wysiwygLinkUrl) return;

    addText(`<${linkUrl.value}|${linkText.value}>`);
    linkButton.value.selected = false;
    linkUrl.value = '';
    linkText.value = '';
  };

  const helpButton = ref({ name: 'help', icon: 'question_mark', selectable: true });

  // Only set a label if it's not the internal name capitalized.
  const extraMarkdownItems = [];
  const autocompletes = props.inputElement.textcomplete?.completer?.strategies?.map(s => s.props.name) || [];
  const usingRoles = autocompletes.some(s => s === 'roles' || s === 'usersAndRoles');
  const usingUsers = autocompletes.some(s => s === 'users' || s === 'usersAndRoles');
  const userLabel = usingRoles ? 'Role/Person' : 'Person';
  // This is a bit of a shit way to differentiate Journeys use cases ("role") and
  // cases like Celebrations that use just "Celebrant". "role" is never really *wrong*, exactly,
  // and the markdown key is pretty hidden anyway, so I'm okay with that possibly slipping through
  // the cracks if a feature ever somehow uses >3 custom "@{" tagged variables.
  if (usingRoles && Donut.Args.roles?.length) {
    if (Donut.Args.roles.length >= 3) { // Teams are initialized with 3 Journeys roles.
      extraMarkdownItems.push({ keyDisplay: '@{role}' });
    } else {
      extraMarkdownItems.push(...Donut.Args.roles.map(r => ({ keyDisplay: `@{${r.name}}` })));
    }
  }

  const buttons = [
    { name: 'bold', icon: 'format_bold', tag: '*', onClick: wrap, keyDisplay: '*bold*', keyComponent: 'strong' },
    { name: 'italics', icon: 'format_italic', tag: '_', onClick: wrap, keyDisplay: '_italics_', keyComponent: 'em' },
    { name: 'strike', icon: 'strikethrough_s', tag: '~', onClick: wrap, keyDisplay: '~strike~' },
    { name: 'quote', icon: 'format_indent_increase', onClick: blockquote, keyDisplay: '>quote' },
    { name: 'code', icon: 'code', tag: '`', onClick: wrap, keyDisplay: '`code`', keyComponent: 'code' },
    linkButton.value,
  ];
  if (autocompletes.some(s => s === 'emoji')) {
    buttons.push({ name: 'emoji', keyDisplay: ':emoji:' });
  }
  if (autocompletes.some(s => s === 'channels')) {
    buttons.push({
      name: 'channels',
      keyDisplay: '#channel',
      // It won't really be a button, but I want it in the markdown key (in the help button).
      formattingKeyOnly: true,
    });
  }
  buttons.push({ divider: true });
  if (usingRoles || usingUsers) {
    buttons.push({
      name: 'users',
      label: userLabel,
      icon: 'alternate_email',
      tag: '@',
      onClick: triggerAutocomplete,
      keyDisplay: '@user',
    });
  }
  if (autocompletes.some(s => s === 'variables') && Donut.Args.otherVariables?.length) {
    buttons.push({
      name: 'variables',
      icon: 'data_object',
      tag: '{',
      onClick: triggerAutocomplete,
    });
    extraMarkdownItems.push(...Donut.Args.otherVariables.map(v => ({ keyDisplay: `{{${v}}}` })));
  }
  buttons.push({ divider: true });
  buttons.push(helpButton.value);
  buttons.forEach(b => { if (!b.divider) b.label = b.label || _.capitalize(b.name); });

  let includedButtons = buttons.filter(b => !props.ignore.includes(b.name));
  includedButtons = includedButtons.filter((b, i) => !(b.divider && includedButtons[i - 1]?.divider));

  const markdownItems = includedButtons.filter(b => b.keyDisplay).concat(extraMarkdownItems);
</script>

<style scoped lang="scss">
  @import '../../../styles/donut';

  .wysiwyg-button { cursor: pointer; }

  .wysiwyg-divider {
    width: 1px;
    height: 20px;
    margin: 0 2px;
    background-color: $light-gray;
  }

  .wysiwyg-button:hover, .wysiwyg-button.selected {
    background-color: $light-gray;

    i.mi {
      opacity: 100%;
    }
  }

  // Instead of giving the emoji component new style options, just restyle it how I need it for this one special case.
  .wysiwyg-button.donut-emoji-selector-button {
    background-color: inherit;
    border: inherit;
    height: inherit;
    width: inherit;

    :deep(i.mi) {
      color: inherit;
      font-size: 20px;
      height: 20px;
      width: 20px;
    }

    &:hover {
      background-color: $light-gray;
    }

    :deep(div:has(> svg)) {
      display: none;
    }
  }
</style>
